import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — attach JWT
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('tg_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Response interceptor — handle 401
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('tg_token');
      localStorage.removeItem('tg_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ───────────────────────────────────────────────────
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  me: () => api.get('/auth/me'),
};

// ── Masters ───────────────────────────────────────────────
export const mastersAPI = {
  getDepartments: () => api.get('/masters/departments'),
  getMultiZones: () => api.get('/masters/multi-zones'),
  getZones: (multiZoneId) => api.get('/masters/zones', { params: { multiZoneId } }),
  getDistricts: (zoneId) => api.get('/masters/districts', { params: { zoneId } }),
  getAllDistricts: () => api.get('/masters/districts'),
  getULBs: (districtId) => api.get('/masters/ulbs', { params: { districtId } }),
  getAllULBs: () => api.get('/masters/ulbs'),
  // Organizations (DTCP) — cadre-based cascade
  // Step 1: Cadres filtered by dept already come from getAllCadres(deptId)
  // Step 2: After designation selected, get cadre categories from organizations
  getOrgCadreCategories: (deptId, nameOfPost) =>
    api.get('/masters/organizations/cadre-categories', { params: { deptId, nameOfPost } }),
  // Step 3: After cadre category selected, get orgs
  getOrgOrgs: (deptId, nameOfPost, cadreCategory) =>
    api.get('/masters/organizations/orgs', { params: { deptId, nameOfPost, cadreCategory } }),
  // Section X preferences — all orgs matching employee's designation+cadre
  getAllOrgs: (deptId, nameOfPost, cadreCategory) =>
    api.get('/masters/organizations/all', { params: { deptId, nameOfPost, cadreCategory } }),
  // Admin org CRUD
  getOrganizations: (deptId) => api.get('/masters/organizations', { params: { deptId } }),
  createOrganization: (data) => api.post('/masters/organizations', data),
  updateOrganization: (id, data) => api.put(`/masters/organizations/${id}`, data),
  deleteOrganization: (id) => api.delete(`/masters/organizations/${id}`),
  uploadOrganizations: (rows) => api.post('/masters/organizations/upload', { rows }),

  // Cadre-filtered ULB loaders — single query each, returns distinct ULBs
  getULBsByZone: (zoneId) => api.get('/masters/ulbs', { params: { zoneId } }),
  getULBsByMultiZone: (multiZoneId) => api.get('/masters/ulbs', { params: { multiZoneId } }),
  getCadres: (cadreType, deptId) => api.get('/masters/cadres', { params: { cadreType, deptId } }),
  getAllCadres: (deptId) => api.get('/masters/cadres', { params: { deptId } }),

  // Admin CRUD
  createMultiZone: (data) => api.post('/masters/multi-zones', data),
  updateMultiZone: (id, data) => api.put(`/masters/multi-zones/${id}`, data),
  deleteMultiZone: (id) => api.delete(`/masters/multi-zones/${id}`),

  createZone: (data) => api.post('/masters/zones', data),
  updateZone: (id, data) => api.put(`/masters/zones/${id}`, data),
  deleteZone: (id) => api.delete(`/masters/zones/${id}`),

  createDistrict: (data) => api.post('/masters/districts', data),
  updateDistrict: (id, data) => api.put(`/masters/districts/${id}`, data),
  deleteDistrict: (id) => api.delete(`/masters/districts/${id}`),

  createULB: (data) => api.post('/masters/ulbs', data),
  updateULB: (id, data) => api.put(`/masters/ulbs/${id}`, data),
  deleteULB: (id) => api.delete(`/masters/ulbs/${id}`),

  createCadre: (data) => api.post('/masters/cadres', data),
  updateCadre: (id, data) => api.put(`/masters/cadres/${id}`, data),
  deleteCadre: (id) => api.delete(`/masters/cadres/${id}`),
};

// ── Applications ──────────────────────────────────────────
export const applicationsAPI = {
  getMyApplication: () => api.get('/applications/my'),
  saveApplication: (data) => api.post('/applications', data),
  submitApplication: (id) => api.post(`/applications/${id}/submit`),
  uploadDocument: (id, docType, file) => {
    const fd = new FormData();
    fd.append(docType, file);
    return api.post(`/applications/${id}/upload/${docType}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Admin
  getAllApplications: (params) => api.get('/applications', { params }),
  getApplicationById: (id) => api.get(`/applications/${id}`),
  exportExcel: () => api.get('/applications/export/excel', { responseType: 'blob' }),
  getPdf: (id) => api.get(`/applications/${id}/pdf`, { responseType: 'blob' }),
  getPdfUrl: (id) => `${BASE_URL}/applications/${id}/pdf`,
  getDocuments: (id) => api.get(`/applications/${id}/documents`),
};

// ── Admin ─────────────────────────────────────────────────
export const adminAPI = {
  getStats: (deptId) => api.get('/admin/stats', { params: deptId ? { deptId } : {} }),
  getUsers: (params) => api.get('/admin/users', { params }),
  createUser: (data) => api.post('/admin/users', data),
  resetPassword: (id) => api.put(`/admin/users/${id}/reset-password`),
  toggleActive: (id) => api.put(`/admin/users/${id}/toggle-active`),
  unlockUser: (id) => api.put(`/admin/users/${id}/unlock`),
  bulkImport: (data) => api.post('/admin/users/bulk-import', data),
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
};

export default api;
